import { getDateFnsLocale } from '@eventbrite/datetime-fns';
import { getAttendeeTimezone } from '@eventbrite/event-date-time';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { format } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { LocalizedDateTime } from '../../../types';
import './StartDate.scss';

type StartDateOldProps = {
    startDateTime: string;
    startMonthAbbr: string;
    startDay: string;
    isSeriesParent: boolean;
};

type StartDateStandardProps = {
    isOnlineEvent: boolean;
    localizedDateTimeStart: LocalizedDateTime;
    locale: string;
    isSeriesParent: boolean;
};

type StartDateProps = {
    startDateTime: string;
    hideStartDate: boolean;
    enableDatetimeStandardization?: boolean;
} & StartDateStandardProps &
    StartDateOldProps;

export const StartDateOld = ({
    startDateTime,
    startMonthAbbr,
    startDay,
    isSeriesParent,
}: StartDateOldProps) => {
    return (
        <time
            className="start-date"
            dateTime={isSeriesParent ? '' : startDateTime}
            data-testid="start-date-old"
        >
            {startMonthAbbr} {startDay}
        </time>
    );
};

const getFormattedStartDate = ({
    localizedDateTimeStart,
    isOnlineEvent,
}: {
    localizedDateTimeStart: LocalizedDateTime;
    isOnlineEvent: boolean;
}) => {
    const date = isOnlineEvent
        ? localizedDateTimeStart.utc
        : localizedDateTimeStart.local;
    const dateObj = new Date(date);
    const timeZone = isOnlineEvent
        ? getAttendeeTimezone() ?? localizedDateTimeStart.timezone
        : localizedDateTimeStart.timezone;
    const shortDate = format(dateObj, 'MMM d', {
        timeZone,
        locale: getDateFnsLocale(),
    });
    const dateTime = format(dateObj, 'yyy-MM-dd', {
        timeZone,
    });

    return { shortDate, dateTime };
};

export const StartDateStandard = ({
    isOnlineEvent,
    localizedDateTimeStart,
    locale,
    isSeriesParent,
}: StartDateStandardProps) => {
    const [formattedDates, setStartDate] = useState(
        isSeriesParent
            ? null
            : getFormattedStartDate({ localizedDateTimeStart, isOnlineEvent }),
    );

    useEffect(() => {
        if (isOnlineEvent && !isSeriesParent) {
            setStartDate(
                getFormattedStartDate({
                    localizedDateTimeStart,
                    isOnlineEvent,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (HAS_WINDOW) {
        window.__i18n__ = { locale };
    }

    return (
        <>
            {isSeriesParent ? (
                <span className="start-date">{gettext('Multiple dates')}</span>
            ) : (
                <time
                    className="start-date"
                    dateTime={formattedDates?.dateTime}
                    data-testid="start-date-standard"
                >
                    {formattedDates?.shortDate}
                </time>
            )}
        </>
    );
};

export const StartDate = ({
    hideStartDate,
    enableDatetimeStandardization,
    startDateTime,
    isOnlineEvent,
    localizedDateTimeStart,
    startMonthAbbr,
    startDay,
    locale,
    isSeriesParent,
}: StartDateProps) => {
    if (hideStartDate) {
        return null;
    }

    return (
        <>
            {enableDatetimeStandardization ? (
                <StartDateStandard
                    isOnlineEvent={isOnlineEvent}
                    localizedDateTimeStart={localizedDateTimeStart}
                    locale={locale}
                    isSeriesParent={isSeriesParent}
                />
            ) : (
                <StartDateOld
                    startDateTime={startDateTime}
                    startMonthAbbr={startMonthAbbr}
                    startDay={startDay}
                    isSeriesParent={isSeriesParent}
                />
            )}
        </>
    );
};
